import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send cookies on every axios request (HttpOnly cookie auth)
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/auth/my-permissions`);
      // Endpoint returns List[str] directly
      setPermissions(Array.isArray(response.data) ? response.data : (response.data?.permissions || []));
    } catch {
      setPermissions([]);
    }
  };

  // Verify session via /auth/me on mount (token is in HttpOnly cookie)
  const verifySession = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      await fetchPermissions();
    } catch {
      setUser(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verifySession();
  }, [verifySession]);

  // Login via backend
  // Response user shape: { id, name, email, image, role, admin, features, market }
  const signInWithEmail = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    await fetchPermissions();
    return response.data;
  };

  const signOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Silent
    }
    setUser(null);
    setPermissions([]);
    toast.success('Logged out successfully');
    window.location.replace('/');
  };

  // Check if the current user has a specific permission
  const hasPermission = (permission) => permissions.includes(permission);

  // Check if the current user has any of the given permissions
  const hasAnyPermission = (perms) => perms.some((p) => permissions.includes(p));

  const value = { user, permissions, loading, signInWithEmail, signOut, hasPermission, hasAnyPermission };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
